import React, { useContext, useEffect } from 'react';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import InputConfig from '../../../../../Components/InputConfig';
import CheckBoxInput from '../../../../../Components/CheckBoxInput';
import DropDownSingle from '../../../../../Components/DropDownSingle';
import { validatePlatformObj } from '../../../../../utilities/tgmrDocChecks';
import { toast } from 'react-toastify';
import constants from '../constants.json';

//mui imports====
import { Grid } from '@mui/material';
//===============

const {
	platforms: { DESKTOP, MOBILE, TABLET },
	tgmrConstants: {
		RESPONSIVE_DESKTOP,
		RESPONSIVE_MOBILE,
		MOBILE_OFC,
		AP_SECTIONID_DESKTOP,
		AP_SECTIONID_MOBILE,
		AP_SECTIONID_CARPET_MOBILE
	}
} = constants;

const PlatformBannerConfig = ({
	keyData,
	platform,
	desktopAdObj = {},
	mobileAdObj = {},
	ofcAdObj = {}
}) => {
	const { rawConfig, updateRawConfig } = useContext(ConfigContext); //rawConfig from ConfigContext

	const mSectionId = rawConfig[keyData + '.' + AP_SECTIONID_MOBILE];
	const ofcSectionId = rawConfig[keyData + '.' + AP_SECTIONID_CARPET_MOBILE];
	const activeSectionId = rawConfig[keyData + '.activeSectionId'];

	// Creating Active Section Id dropdown options array
	let options = [
		{ label: 'mobile sectionId', value: mSectionId },
		{ label: 'flyingCarpet sectionId', value: ofcSectionId }
	];

	useEffect(() => {
		// Check if mobile section Id is present and activeSectionId is not present in config then only updates it, if activeSection is present then use the value from config itself.
		if (mSectionId && !activeSectionId) {
			let sectionId = mSectionId;

			updateRawConfig((rawObj) => {
				let deepClonedObj = structuredClone(rawObj);
				return { ...deepClonedObj, [keyData + '.activeSectionId']: sectionId };
			});
		}
	}, [mSectionId, activeSectionId]);

	/**
	 * @name onDesktopSectionIdEnabled
	 * @description The function is used to enable the desktop section Id for BVS, it has several conditions which are to be fulfilled to enable the BVS
	 * 1. check desktop Ap Tag object and desktop section Id
	 * @param {Object} enabled - It is an enabled flag whether to enable desktop section in BVS or not
	 */
	const onDesktopSectionIdEnabled = (enabled) => {
		try {
			//1. Validating Desktop Ad object and desktop section Id
			validatePlatformObj(
				desktopAdObj,
				platform,
				rawConfig[keyData + '.' + AP_SECTIONID_DESKTOP],
				RESPONSIVE_DESKTOP,
				AP_SECTIONID_DESKTOP
			);

			updateRawConfig((rawObj) => {
				let deepClonedObj = structuredClone(rawObj);
				return { ...deepClonedObj, [keyData + '.enabled']: enabled };
			});
		} catch (err) {
			toast.error(err.message, { position: toast.POSITION.BOTTOM_RIGHT });
			console.error(err);
		}
	};

	/**
	 * @name onMobileSectionIdEnabled
	 * @description The function is used to enable the mobile section Id for BVS, it has several conditions which are to be fulfilled to enable the BVS
	 * 1. check mobile Ap Tag object and mobile section Id
	 * @param {Object} enabled - It is an enabled flag whether to enable mobile section in BVS or not
	 */
	const onMobileSectionIdEnabled = (enabled) => {
		try {
			//1. Validating Mobile Ad object and mobile section Id
			validatePlatformObj(
				mobileAdObj,
				platform,
				mSectionId,
				RESPONSIVE_MOBILE,
				AP_SECTIONID_MOBILE
			);

			updateRawConfig((rawObj) => {
				let deepClonedObj = structuredClone(rawObj);
				return { ...deepClonedObj, [keyData + '.enabled']: enabled };
			});
		} catch (err) {
			toast.error(err.message, { position: toast.POSITION.BOTTOM_RIGHT });
			console.error(err);
		}
	};

	/**
	 * @name onActiveSectionIdChange
	 * @description The function is used to update the active section Id for BVS, it has several conditions which are to be fulfilled to update section Id for BVS
	 * 1. check mobile Ap Tag object and mobile section Id
	 * 2. check optimized FlyingCarpet Ap Tag object and ofc section Id
	 * 3. check if update value is valid
	 * @param {Object} enabled - It is an enabled flag whether to enable mobile section in BVS or not
	 */
	const onActiveSectionIdChange = (updatedId) => {
		try {
			// 1. Validating Mobile Ad object and mobile section Id
			validatePlatformObj(
				mobileAdObj,
				platform,
				mSectionId,
				RESPONSIVE_MOBILE,
				AP_SECTIONID_MOBILE
			);

			// 2. Validating Optimized flying carpet object and section Id
			validatePlatformObj(ofcAdObj, platform, ofcSectionId, MOBILE_OFC, AP_SECTIONID_CARPET_MOBILE);

			// 3. check if the updateId is valid then update it as activeSectionId else throw error
			if (!updatedId) {
				throw new Error(`Cannot update value as the ${updatedId} is missing in the config`);
			}

			updateRawConfig((rawObj) => {
				let deepClonedObj = structuredClone(rawObj);
				return { ...deepClonedObj, [keyData + '.activeSectionId']: updatedId };
			});
		} catch (err) {
			toast.error(err.message, { position: toast.POSITION.BOTTOM_RIGHT });
			console.error(err);
		}
	};

	return (
		<Grid container spacing={1} style={{ padding: '1rem' }}>
			{platform === DESKTOP && (
				<>
					<Grid item xs={2}>
						<CheckBoxInput
							keyData={keyData + '.enabled'}
							label="enabled"
							disabled={false}
							defaultChecked={false}
							isObjectEditor={false}
							directValue={rawConfig[keyData + '.enabled']}
							updateDirectValue={(enabled) => {
								onDesktopSectionIdEnabled(enabled);
							}}
							containerStyles={{ width: 'fit-content', marginTop: '4rem' }}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="Desktop SectionId"
							keyData={keyData + '.' + AP_SECTIONID_DESKTOP}
							editable={false}
							isObjectEditor={false}
							directValue={rawConfig[keyData + '.' + AP_SECTIONID_DESKTOP]}
							updateDirectValue={() => {}}
						/>
					</Grid>
				</>
			)}
			{(platform === MOBILE || platform === TABLET) && (
				<>
					<Grid item xs={2}>
						<CheckBoxInput
							keyData={keyData + '.enabled'}
							label="enabled"
							disabled={false}
							defaultChecked={false}
							isObjectEditor={false}
							directValue={rawConfig[keyData + '.enabled']}
							updateDirectValue={(enabled) => {
								onMobileSectionIdEnabled(enabled);
							}}
							containerStyles={{ width: 'fit-content', marginTop: '4rem' }}
						/>
					</Grid>
					<Grid item xs={3} style={{ width: '70' }}>
						<DropDownSingle
							keyData={keyData + '.activeSectionId'}
							list={options}
							label="Enable Active Section Id"
							directValue={activeSectionId}
							isObjectEditor={false}
							updateDirectValue={(updatedId) => onActiveSectionIdChange(updatedId)}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="Mobile SectionId"
							keyData={keyData + '.' + AP_SECTIONID_MOBILE}
							editable={false}
							isObjectEditor={false}
							directValue={mSectionId}
							updateDirectValue={() => {}}
						/>
					</Grid>

					<Grid item xs={3}>
						<InputConfig
							label="FlyingCarpet SectionId"
							keyData={keyData + '.' + AP_SECTIONID_CARPET_MOBILE}
							editable={false}
							isObjectEditor={false}
							directValue={ofcSectionId}
							updateDirectValue={() => {}}
						/>
					</Grid>
				</>
			)}
		</Grid>
	);
};

export default PlatformBannerConfig;
